import FileDownload from "@mui/icons-material/FileDownload";
import {
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { ExportDataButton } from "components/ExportDataButton";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useCurrentUser } from "hooks/useCurrentUser";
import { ExportParams, useExportData } from "hooks/useExportData";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useCallback, useState } from "react";

import { ExportManifestPdfsDialog } from "./ExportManifestPdfsDialog";
import { manifestsExportFileName } from "./manifestsExportFileName";

type Props = {
  tenantId: string;
  search: string;
  selectedIds: string[];
  /**
   * The vendor dashboard's shipped-year filter. The optional vendor-stats
   * sheet rolls up over this year, mirroring what the dashboard shows.
   */
  shippedDateYear: number;
  /**
   * Staff-only. Controls whether the menu surfaces the discrepancy export and
   * its options dialog. When false we fall back to the plain manifest export
   * button so non-staff users see the same affordance as before.
   */
  showDiscrepancyCounts: boolean;
  /**
   * Staff only for now (ENG-16005); flips to customer-facing later. Controls
   * whether the menu surfaces the "Export Manifest PDFs…" item and dialog.
   */
  showManifestPdfExport: boolean;
};

export function ManifestsExportMenu({
  tenantId,
  search,
  selectedIds,
  shippedDateYear,
  showDiscrepancyCounts,
  showManifestPdfExport,
}: Props) {
  const fileName = manifestsExportFileName(search);
  const { featureFlags } = useFeatureFlags();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showDiscrepancyDialog, setShowDiscrepancyDialog] = useState(false);
  const [showPdfExportDialog, setShowPdfExportDialog] = useState(false);
  const [includeChangeHistory, setIncludeChangeHistory] = useState(false);
  const [includeVendorStats, setIncludeVendorStats] = useState(false);
  const menuOpen = Boolean(anchorEl);
  // The vendor-stats sheet mirrors the vendor dashboard, which only exists
  // behind this flag — hide the toggle wherever the dashboard is hidden.
  const showVendorStatsToggle = featureFlags[FeatureFlagKey.WasteEpaCorrectionDiscrepancies];

  const { isStaff: viewerIsStaff } = useCurrentUser();
  const { startExport, status } = useExportData({ autoDownload: true, progressMode: "tray" });
  const isExporting = status === "loading";

  const closeMenu = () => setAnchorEl(null);

  const runManifestExport = useCallback(() => {
    closeMenu();
    const params: ExportParams = {
      exportType: "wasteManifests",
      tenantId,
      fileName,
      filter: { search, viewerIsStaff },
      trayTitle: "Manifest export",
    };
    startExport(params);
  }, [tenantId, fileName, search, startExport, viewerIsStaff]);

  const runDiscrepancyExport = useCallback(() => {
    setShowDiscrepancyDialog(false);
    const params: ExportParams = {
      exportType: "manifestDiscrepancies",
      tenantId,
      fileName: `${fileName}-discrepancies`,
      filter: {
        // The export ANDs the search with the ids, and a selection can hold
        // manifests the current search no longer matches.
        ...(selectedIds.length > 0
          ? { search: "", processedManifestIds: selectedIds }
          : { search }),
        ...(includeChangeHistory ? { includeChangeHistory: true } : {}),
        ...(includeVendorStats
          ? { includeVendorStats: true, statsShippedYear: shippedDateYear }
          : {}),
      },
      trayTitle: "Discrepancy export",
    };
    startExport(params);
  }, [
    tenantId,
    fileName,
    search,
    selectedIds,
    includeChangeHistory,
    includeVendorStats,
    shippedDateYear,
    startExport,
  ]);

  // Non-staff / flag off: preserve the original single-purpose button so we
  // don't change the UX for anyone who can't see discrepancies anyway. Staff
  // still get the menu when only the PDF export is available.
  if (!showDiscrepancyCounts && !showManifestPdfExport) {
    return (
      <ExportDataButton
        fileName={fileName}
        tenantId={tenantId}
        filter={{ search, viewerIsStaff }}
        exportType="wasteManifests"
        iconOnly
      />
    );
  }

  const discrepancyLabel =
    selectedIds.length > 0
      ? `Export Discrepancies (${selectedIds.length})…`
      : "Export Discrepancies…";

  return (
    <>
      <Tooltip title="Export">
        <IconButton
          size="medium"
          color="info"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          loading={isExporting}
          aria-controls={menuOpen ? "manifests-export-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? "true" : undefined}
        >
          <FileDownload />
        </IconButton>
      </Tooltip>
      <Menu
        id="manifests-export-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={runManifestExport}>Export Manifests</MenuItem>
        {showDiscrepancyCounts && (
          <MenuItem
            onClick={() => {
              closeMenu();
              setShowDiscrepancyDialog(true);
            }}
          >
            {discrepancyLabel}
          </MenuItem>
        )}
        {showManifestPdfExport && (
          <MenuItem
            onClick={() => {
              closeMenu();
              setShowPdfExportDialog(true);
            }}
          >
            Export Manifest PDFs…
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={showDiscrepancyDialog}
        onClose={() => setShowDiscrepancyDialog(false)}
        title="Export Discrepancies"
        description={
          selectedIds.length > 0
            ? `Exports discrepancies for the ${selectedIds.length} selected manifest${
                selectedIds.length === 1 ? "" : "s"
              }.`
            : "Exports discrepancies for every manifest matching the current filter."
        }
        maxWidth="sm"
        fullWidth
        primaryActionButtonProps={{
          saveText: "Export",
          onClick: runDiscrepancyExport,
          hideStartIcon: true,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={includeChangeHistory}
              onChange={(e) => setIncludeChangeHistory(e.target.checked)}
            />
          }
          label="Include change history"
        />
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          Adds a second sheet with one row per discrepancy event (opens, resolves, re-opens, and
          value changes). Useful for auditing resolution timelines; adds file size and generation
          time.
        </Typography>
        {showVendorStatsToggle && (
          <>
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Switch
                  checked={includeVendorStats}
                  onChange={(e) => setIncludeVendorStats(e.target.checked)}
                />
              }
              label="Include vendor stats"
            />
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              Adds a sheet with the vendor dashboard&apos;s {shippedDateYear} stats — 30-day
              correction compliance, days to correction closure, and Encamp vs regulator flagged —
              including the monthly and per-vendor breakdowns.
            </Typography>
          </>
        )}
      </Dialog>

      <ExportManifestPdfsDialog
        open={showPdfExportDialog}
        onClose={() => setShowPdfExportDialog(false)}
      />
    </>
  );
}
